import { create } from "zustand";
import { closeWebSocket, getSessionId, initializeWebSocket, sendWebSocketMessage } from "@akavirtual/deai-web-core/services"
import { toast } from "sonner";
import { IdleCountdownManager } from "@/lib/idle-manager";
import type { BottleType, BottleColor, EffectType } from "./tgs2025/potionmaker/use-video-manager";
import type { PersonaId } from "@/types/persona";
import { DEFAULT_PERSONA } from "@/types/persona";

export interface ShowFortuneParams {
  fortuneText: string;
  fortuneLevel: string;
  locale?: 'EN' | 'JA';
  ratings: {
    overall: number;
    romance: number;
    health: number;
    money: number;
  };
}
// import { devtools } from 'zustand/middleware'

export type SystemEventPayload = {
  action: string,
  data: {
    triggerEvent?: string,
    type?: string,
    action?: string,
    ingredient?: string,
    buttonId?: string,
    text?: string,
    gamePhase?: string,
    [key: string]: unknown,
  }
}

const handlers: Map<string, Set<(payload?: object) => void>> = new Map<string, Set<(payload?: object) => void>>();

const pub = (type:string, payload?:object) => {
    handlers.get(type)?.forEach(callback => callback(payload));
}

export interface PhotoboothNavigationData {
  sourceExperience: 'fortune-telling' | 'potion-craft';
  overlayImage?: string;
  theme?: string;
  data?: Record<string, any>; // Additional data specific to each experience
}

export interface AppState {
  sessionId: string | null;
  isStarted:boolean,
  isConnected: boolean,
  isConnecting: boolean,
  isReconnecting: boolean,
  qosMessage: { text: string; id: number } | null; // Added ID for unique toast key
  isTyping: boolean; // Controls typing indicator
  contextProvider: (()=>object) | null;
  persona: PersonaId;
  fortuneDisplay: {
    isVisible: boolean;
    fortuneData: ShowFortuneParams | null;
  };
  photoboothNavigation: PhotoboothNavigationData | null;
}

export interface AppActions {
  initialize: (contextProvider?: ()=>object, persona?: PersonaId) => Promise<void>;
  connectWebSocket: () => Promise<void>;
  reconnect: () => void;
  disconnectWebSocket: () => void;

  start: ()=> void;
  resetExperience: () => void; // Reset the experience due to idle timeout
  sendMessage: (payload: object ) => void; // For text input (if added later)
  sendMessageWithContext: (content: string) => void; // Send message with current context
  sendContextUpdate: () => void; // Send context update to backend
  sendAudio: (blob: Blob) => void;
  stopAudio: () => void;

  hideQosMessage: ()=>void;
  showQosMessage: (text: string, duration?: number) => void;

  sub: (messageType:string, handler:(payload?:object)=>void ) => void;
  unsub: (messageType:string, handler:(payload?:object)=>void ) => void;

  triggerAvatarAnimator: (index:number, trigger:string) => void;
  triggerAvatarExpression: (index:number, expressionName:string, weight?:number) => void;
  
  // Video trigger methods for potion maker
  triggerPotionVideo: (bottleType: BottleType, color: BottleColor) => void;
  triggerEffectVideo: (effectType: EffectType) => void;
  triggerCustomVideo: (videoPath: string) => void;
  triggerLoopingVideo: (videoPath: string) => void; // New method for seamless looping videos
  triggerLoopedPotionVideo: (bottleType: BottleType, color: BottleColor, loopLastSeconds: number, loopDuration: number) => void;
  triggerCauldronSmokeEffect: () => void; // New trigger for cauldron smoke effect
  
  // Fortune display actions
  showFortune: (data: ShowFortuneParams) => void;
  hideFortune: () => void;

  // Photobooth navigation actions
  setPhotoboothNavigationData: (data: PhotoboothNavigationData) => void;
  clearPhotoboothNavigationData: () => void;

}

let typingTimeout: ReturnType<typeof setTimeout> | null = null;


let reconnectTimer: NodeJS.Timeout | null = null;
let reconnectAttempts = 0;

export type AppStore = AppState & AppActions;

const initialSessionId = localStorage.getItem("sid");

const initialState: AppState = {
  sessionId: initialSessionId,
  isStarted: false,
  isConnected: false,
  isConnecting: false,
  isReconnecting: false,
  qosMessage: null,
  isTyping: false,
  contextProvider: null,
  persona: DEFAULT_PERSONA,
  fortuneDisplay: {
    isVisible: false,
    fortuneData: null,
  },
  photoboothNavigation: null,
};

export const useAppStore = create<AppStore>()(
  // immer( // Uncomment if using immer middleware
// devtools(
  (set, get) => ({
    ...initialState,
    initialize : async (contextProvider?: ()=>object, persona?: PersonaId) => {
      set({ 
        contextProvider,
        persona: persona || DEFAULT_PERSONA
      });
      await get().connectWebSocket();
    },
    // --- ACTIONS ---
    connectWebSocket: async () => {
        const isConnecting = get().isConnecting;
        if(isConnecting) {
            return;
        }
        // console.log(`isConnecting = ${isConnecting}`)
        const currentSessionId = get().sessionId;
        let targetSessionId = currentSessionId;
        if(!currentSessionId) {
            set({ isConnecting: true, isReconnecting: false });
            const sessionData = await getSessionId();
            console.log(sessionData);
            if (!sessionData) 
              throw new Error("Failed to obtain session.");
            targetSessionId = sessionData.sessionId;
            localStorage.setItem("sid", targetSessionId);
        } else if (!targetSessionId) {
            console.warn("Cannot connect: No room ID or session ID available.");
            // Maybe attempt to get a new session implicitly?
            // For now, just return.
            return;
        }

        // If already connected, disconnect first (unless it's the same session)
        if (get().isConnected && currentSessionId !== targetSessionId) {
            closeWebSocket();
            set({ isConnected: false });
        } else if (get().isConnected && currentSessionId === targetSessionId) {
            console.log("Already connected to the same session.");
            IdleCountdownManager.updateActivity();
            set({ isConnecting: false, isReconnecting: false  }); // Ensure loading state is off
            return; // Already connected
        }

        set({ isConnecting: true, isReconnecting: false });

        initializeWebSocket(targetSessionId, {
            onOpen: () => {
            console.log("WebSocket Connected");
            // On successful connection, reset reconnect attempts
            if (reconnectTimer) clearTimeout(reconnectTimer);
            reconnectAttempts = 0;
            set({ isConnected: true, isConnecting: false, isReconnecting: false });
            // Initial connection established - ready for user to start
            //   if (isNewRoom) {
            //     set({ messages: [], hasChatlog: false }); // Clear chat on new room join
            //   }
            get().showQosMessage("Connected!", 2000);
            //   // Trigger tutorial if needed
            //   if (!get().hasSeenTutorial && !get().hasChatlog) {
            //     // TODO: Implement tutorial logic here or in a component effect
            //     // showTutorial(set); // Pass set to update state during tutorial
            //     localStorage.setItem("tut", "true");
            //     set({ hasSeenTutorial: true });
            //   }
            },
            onMessage: (data: unknown | Blob) => {
              if (typingTimeout) clearTimeout(typingTimeout);
              set({ isTyping: false }); // Hide typing indicator on any message

            if (data instanceof Blob) {
                console.log("Received raw audio blob");
                // pub('audio', data);
                // TODO: Trigger audio playback hook/logic
                // useAudioPlayer.getState().queueAudio(data);
            } else if(data instanceof Object && Object.prototype.hasOwnProperty.call(data, 'action')) {
                // console.log("Received message:", data);
                const { action } = data as {action:string};
                if (action === "audio") {
                  // Stop audio to prevent lipsync issue
                  /*
                  pub('vision', {
                    cmd:'avatar/ability/speek',
                    payload:{
                      index:0,
                      interrupt:true
                    }
                  });
                  */
                }
                pub(action, data);
                if(action === "chatlog_clear") {
                  set({ isStarted: false }); // Reset to not started state
                  // Also clear all frontend state when chatlog is cleared
                  pub('clear_frontend', {});
                }
            } else {
                console.log("Received unknown message:", data);
            }
            // const currentMessages = get().messages;
            },
            onClose: (event: CloseEvent) => {
              console.log("WebSocket Closed:", event.code, event.reason);
              // get().showQosMessage("WebSocket Closed!", 2000);
              // console.log("WebSocket Closed.");
              set({ isConnected: false, isConnecting: false });
              // When the connection closes, just call the reconnect action
              get().reconnect();
            },
            onError: (error: Event) => {
              console.error("WebSocket Error:", error);
              set({ isConnected: false, isConnecting: false });
              // When an error occurs, also call the reconnect action
              get().reconnect();
              // Clear potentially invalid session ID on error
              localStorage.removeItem("sid");
              set({ sessionId: null });
            },
        });
    },

    reconnect: () => {
     const { isConnected, isConnecting } = get();
      // Don't try to reconnect if already connected/connecting, or if the browser is offline
      if (isConnecting || !navigator.onLine) {
        return;
      }

      set({ isReconnecting: true });

      if(isConnected) {
          get().resetExperience();
      } else {
      // Exponential backoff logic
      const delay = Math.min(1000 * 2 ** reconnectAttempts, 30000); // Max 30s delay

      if(reconnectTimer) clearTimeout(reconnectTimer);

      reconnectTimer = setTimeout(() => {
          console.log(`Attempting to reconnect (attempt ${reconnectAttempts + 1})...`);
          get().connectWebSocket();
          reconnectAttempts++;
        }, delay);
      }

    },

    disconnectWebSocket: () => {
      closeWebSocket();
      set({ isConnected: false, isConnecting: false, isReconnecting: false });
    },

    sendAudio: (blob: Blob) => {
      if (!get().isConnected) {
        console.warn("Cannot send audio: WebSocket not connected.");
        get().showQosMessage("Not connected.", 2000);
        return;
      }
      console.log("Sending audio blob:", blob.size, "bytes");
      IdleCountdownManager.updateActivity();
      sendWebSocketMessage(blob); // Send blob directly
      set({ isTyping: true }); // Show typing indicator
      if (typingTimeout) clearTimeout(typingTimeout);
      typingTimeout = setTimeout(() => set({ isTyping: false }), 5000);
    },

    stopAudio: () => {
      pub('vision', {
        cmd:'avatar/ability/speek',
        payload:{
          index:0,
          interrupt:true
        }
      })
    },
    
    start: () => {
      if(get().isConnected && !get().isStarted) {
        const { persona } = get();
        get().sendMessage({ action: 'start', persona });
        set({isStarted:true});
        IdleCountdownManager.updateActivity(); // Initialize activity tracking when starting
        // Send initial context update when connected
        setTimeout(() => {
          get().sendContextUpdate();
        }, 100);

      }
    },

    resetExperience: () => {
      if(get().isConnected) {
        get().sendMessage({ 
          action: 'system', 
          type: 'idle_reset',
          content: 'Reset experience due to idle timeout'
        });
        set({isStarted: false}); // Reset to not started state
        IdleCountdownManager.cancel(); // Cancel any ongoing idle countdown
        
        // Publish event to clear all frontend state (cart, filters, etc.)
        pub('clear_frontend', {});
        window.location.replace('/');
      }
    },

    sendMessage: (payload: object ) => {
        if(get().isConnected) {
            IdleCountdownManager.updateActivity();
            sendWebSocketMessage(payload);
        }
    },

    sendMessageWithContext: (content: string) => {
        if(get().isConnected) {
          const contextData = get().contextProvider?.();
          get().sendMessage({
            action: 'message',
            role: 'user',
            content,
            contextData,
          });
        }
    },

    sendContextUpdate: () => {
        if(get().isConnected) {
          const contextData = get().contextProvider?.();
          get().sendMessage({
            action: 'context_update',
            contextData,
          });
        }
    },

    showQosMessage: (text: string, duration: number = 3000) => {
      const id = Date.now(); // Unique ID for the toast
      toast(text, {
        id: id,
        duration: duration === -1 ? Infinity : duration, // Show indefinitely if duration is -1
      });
      // We don't strictly need to store it in state if using sonner,
      // but kept for potential direct rendering if needed.
      set({ qosMessage: { text, id } });
    },

    hideQosMessage() {
      const qosMessage = get().qosMessage;
      if(qosMessage) {
        toast.dismiss(qosMessage.id);
      }
    },

    sub: (messageType:string, handler:(payload?:object)=>void ) => {
      if (!handlers.has(messageType)) {
        handlers.set(messageType, new Set());
      }
      handlers.get(messageType)!.add(handler);
    },

    unsub: (messageType:string, handler:(payload?:object)=>void ) => {
      handlers.get(messageType)?.delete(handler);
    },

    triggerAvatarAnimator: (index:number, trigger:string) => {
      pub('vision', {
        cmd:'avatar/animator/trigger/set',
        payload: {
         index,
         trigger
        }
      })
    },

    triggerAvatarExpression: (index:number, expressionName:string, weight:number = 1.0) => {
      console.log(`Setting VRM expression: ${expressionName} with weight: ${weight} for avatar: ${index}`);
      
      // Direct VRM access method - this is the working approach
      try {
        const debugRef = (window as any).debugDEAIRef;
        if (debugRef?.current?._webVision?._avatars?.[index]?.currentVrm?.expressionManager) {
          const expressionManager = debugRef.current._webVision._avatars[index].currentVrm.expressionManager;
          expressionManager.setValue(expressionName, weight);
          console.log(`✅ Successfully set ${expressionName} expression via direct VRM access`);
          return; // Success, don't try the message method
        }
      } catch (e) {
        console.warn('Direct VRM access failed, falling back to message method:', e);
      }
      
      // Fallback to message method (this doesn't work yet, but keeping for future compatibility)
      pub('vision', {
        cmd:'avatar/expression/set',
        payload: {
         index,
         expressionName,
         weight
        }
      });
    },

    // Video trigger methods for potion maker
    triggerPotionVideo: (bottleType: BottleType, color: BottleColor) => {
      pub('video_trigger', {
        type: 'potion',
        bottleType,
        color
      });
    },

    triggerEffectVideo: (effectType: EffectType) => {
      pub('video_trigger', {
        type: 'effect',
        effectType
      });
    },

    triggerCustomVideo: (videoPath: string) => {
      pub('video_trigger', {
        type: 'custom',
        videoPath
      });
    },

    triggerLoopingVideo: (videoPath: string) => {
      pub('video_trigger', {
        type: 'looping',
        videoPath
      });
    },

    triggerLoopedPotionVideo: (bottleType: BottleType, color: BottleColor, loopLastSeconds: number, loopDuration: number) => {
      pub('video_trigger', {
        type: 'looped',
        bottleType,
        color,
        loopLastSeconds,
        loopDuration
      });
    },

    triggerCauldronSmokeEffect: () => {
      pub('cauldron_smoke_effect', {});
    },

    showFortune: (data: ShowFortuneParams) => {
      set({
        fortuneDisplay: {
          isVisible: true,
          fortuneData: data,
        }
      });
    },

    hideFortune: () => {
      set({
        fortuneDisplay: {
          isVisible: false,
          fortuneData: null,
        }
      });
    },

    setPhotoboothNavigationData: (data: PhotoboothNavigationData) => {
      set({ photoboothNavigation: data });
    },

    clearPhotoboothNavigationData: () => {
      set({ photoboothNavigation: null });
    },

  })
// )
);